/**
 * Spell Entries as the engine reads them: base rank, cantrip and focus flags, traditions, the
 * rank a spell is cast at, and heightening (Foundry: item/spell/document.ts). A fixed
 * heightening layer merges its overrides onto the spell's system data for every level at or
 * below the cast rank; interval heightening appends the scaling damage once per interval
 * crossed and grows the area. Overlays (a spell's alternative casting modes) are listed by name
 * and otherwise left to the Entry viewer (#14).
 */
import type { Entry } from "../../shared/snapshot-format";
import type { MagicTradition } from "../character/model";

const TRADITIONS = ["arcane", "divine", "occult", "primal"] as const;

export function spellTraits(entry: Entry): string[] {
  const value = (entry.system.traits as { value?: unknown } | undefined)?.value;
  return Array.isArray(value) ? value.filter((t): t is string => typeof t === "string") : entry.traits;
}

export function spellTraditions(entry: Entry): MagicTradition[] {
  const value = (entry.system.traits as { traditions?: unknown } | undefined)?.traditions;
  return Array.isArray(value) ? value.filter((t): t is MagicTradition => (TRADITIONS as readonly string[]).includes(String(t))) : [];
}

/** The spell's rank before heightening (`system.level.value`, clamped to 1 to 10). */
export function spellBaseRank(entry: Entry): number {
  const value = (entry.system.level as { value?: unknown } | undefined)?.value;
  return typeof value === "number" && Number.isFinite(value) ? Math.min(10, Math.max(1, Math.trunc(value))) : 1;
}

export function isCantrip(entry: Entry): boolean {
  return spellTraits(entry).includes("cantrip");
}

/** Foundry's `isFocusSpell`: the focus trait, or a cantrip with no tradition (a focus cantrip). */
export function isFocusSpell(entry: Entry): boolean {
  const traits = spellTraits(entry);
  return traits.includes("focus") || (spellTraditions(entry).length === 0 && traits.includes("cantrip"));
}

export function isRitual(entry: Entry): boolean {
  return entry.system.ritual !== null && entry.system.ritual !== undefined;
}

/** Cantrips and focus spells heighten to half the Level rounded up (Foundry: `SpellPF2e#rank`). */
export function autoHeightenRank(level: number): number {
  return Math.min(10, Math.max(1, Math.ceil(level / 2)));
}

/**
 * The rank a spell is cast at: cantrips and focus spells auto-heighten with the Level; other
 * spells are cast at the slot's rank (prepared) or the rank they are known at (spontaneous,
 * innate), never below their base rank.
 */
export function castRank(entry: Entry, level: number, slotRank: number | null | undefined): number {
  const base = spellBaseRank(entry);
  if (isCantrip(entry) || isFocusSpell(entry)) return Math.max(base, autoHeightenRank(level));
  return Math.min(10, Math.max(base, slotRank ?? base));
}

export interface HeightenedDamage {
  id: string;
  formula: string;
  type: string;
  category: string | null;
  kinds: string[];
}

export interface HeightenedSpell {
  rank: number;
  /** The spell's system data with the heightening in force applied. */
  system: Record<string, unknown>;
  damage: HeightenedDamage[];
  /** What heightening changed, in words ("target: 5 creatures", "area: 30-foot burst"), for the sheet. */
  changes: string[];
  /** How many intervals of interval heightening applied. */
  timesHeightened: number;
  /** Names of the spell's override overlays (alternative casting modes), if any. */
  overlays: string[];
}

type Term = { faces: number; count: number } | { constant: number };

/** A simple damage formula ("2d4", "1d8+8", "4") as dice and constant terms; null when it is not simple. */
export function parseSimpleFormula(formula: string): Term[] | null {
  const terms: Term[] = [];
  const cleaned = formula.replace(/\s+/g, "");
  if (cleaned === "" || cleaned === "0") return [];
  const re = /([+-]?)(\d*)d(\d+)|([+-]?)(\d+)/gy;
  let index = 0;
  while (index < cleaned.length) {
    re.lastIndex = index;
    const m = re.exec(cleaned);
    if (!m || m.index !== index) return null;
    const sign = (m[1] ?? m[4] ?? "") === "-" ? -1 : 1;
    if (m[3]) terms.push({ faces: Number(m[3]), count: sign * (m[2] ? Number(m[2]) : 1) });
    else terms.push({ constant: sign * Number(m[5]) });
    index = m.index + m[0].length;
  }
  return terms;
}

/** Foundry's `combinePartialTerms`: dice of one size add up, constants add up. */
export function combineTerms(terms: Term[]): string {
  const dice = new Map<number, number>();
  let constant = 0;
  for (const t of terms) {
    if ("constant" in t) constant += t.constant;
    else dice.set(t.faces, (dice.get(t.faces) ?? 0) + t.count);
  }
  const parts = [...dice.entries()].filter(([, n]) => n !== 0).sort((a, b) => b[0] - a[0]).map(([faces, n]) => `${n}d${faces}`);
  if (constant !== 0 || parts.length === 0) parts.push(String(constant));
  return parts.join("+").replace(/\+-/g, "-");
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Foundry's `mergeObject` with overwrite: nested records merge, everything else replaces. */
function merge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(source)) {
    const current = out[key];
    out[key] = isRecord(value) && isRecord(current) ? merge(current, value) : structuredClone(value);
  }
  return out;
}

const ORDINAL_WORDS = ["", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];

export function rankLabel(rank: number): string {
  return rank === 0 ? "cantrip" : `${ORDINAL_WORDS[rank] ?? `${rank}th`} rank`;
}

function describeArea(area: unknown): string | null {
  if (!isRecord(area) || typeof area.value !== "number") return null;
  return `${area.value}-foot ${typeof area.type === "string" ? area.type : "area"}`;
}

/** The spell at a rank: fixed layers merged, interval damage appended, with the changes listed. */
export function heightenSpell(entry: Entry, rank: number): HeightenedSpell {
  const base = spellBaseRank(entry);
  const target = Math.min(10, Math.max(base, rank));
  let system = structuredClone(entry.system);
  const changes: string[] = [];
  const heightening = isRecord(system.heightening) ? system.heightening : null;
  let timesHeightened = 0;

  if (heightening?.type === "fixed" && isRecord(heightening.levels)) {
    const layers = Object.entries(heightening.levels)
      .map(([level, data]) => ({ level: Number(level), data }))
      .filter((l) => Number.isFinite(l.level) && l.level <= target && isRecord(l.data))
      .sort((a, b) => a.level - b.level);
    for (const layer of layers) {
      const data = layer.data as Record<string, unknown>;
      system = merge(system, data);
      for (const [key, value] of Object.entries(data)) {
        if (key === "damage" || key === "description" || key === "traits") continue;
        const text = isRecord(value) && typeof value.value === "string" ? value.value : isRecord(value) && key === "area" ? describeArea(value) : typeof value === "string" ? value : null;
        if (text) changes.push(`${key}: ${text}`);
      }
      if (isRecord(data.traits) && Array.isArray(data.traits.value)) changes.push(`traits: ${(data.traits.value as unknown[]).join(", ")}`);
    }
  } else if (heightening?.type === "interval" && typeof heightening.interval === "number" && heightening.interval > 0) {
    timesHeightened = Math.floor((target - base) / heightening.interval);
    if (timesHeightened > 0 && isRecord(system.area) && typeof system.area.value === "number" && typeof heightening.area === "number" && heightening.area > 0) {
      system.area = { ...system.area, value: system.area.value + heightening.area * timesHeightened };
      changes.push(`area: ${describeArea(system.area)}`);
    }
  }

  const damage: HeightenedDamage[] = [];
  const damageRecord = isRecord(system.damage) ? system.damage : {};
  for (const [id, raw] of Object.entries(damageRecord)) {
    if (!isRecord(raw) || typeof raw.formula !== "string") continue;
    let formula = raw.formula.trim() || "0";
    const scaling = heightening?.type === "interval" && isRecord(heightening.damage) && typeof heightening.damage[id] === "string" ? (heightening.damage[id] as string).trim() : "";
    if (timesHeightened > 0 && scaling && scaling !== "0") {
      const baseTerms = parseSimpleFormula(formula);
      const scalingTerms = parseSimpleFormula(scaling);
      if (baseTerms && scalingTerms) {
        const all = [...baseTerms];
        for (let i = 0; i < timesHeightened; i++) all.push(...scalingTerms);
        formula = combineTerms(all);
      } else {
        formula = [formula, ...Array<string>(timesHeightened).fill(scaling)].join(" + ");
      }
    }
    const kinds = Array.isArray(raw.kinds) ? raw.kinds.filter((k): k is string => typeof k === "string") : ["damage"];
    damage.push({ id, formula, type: typeof raw.type === "string" ? raw.type : "untyped", category: typeof raw.category === "string" ? raw.category : null, kinds: kinds.length > 0 ? kinds : ["damage"] });
  }
  if (timesHeightened > 0 && damage.length > 0) changes.push(`damage: ${damage.map((d) => `${d.formula} ${d.type}`).join(", ")}`);

  const overlays = isRecord(system.overlays) ? Object.values(system.overlays).filter(isRecord).filter((o) => o.overlayType === "override").map((o) => (typeof o.name === "string" && o.name ? o.name : "variant")) : [];
  return { rank: target, system, damage, changes, timesHeightened, overlays };
}
